import {
  ArrowUUpLeftIcon,
  ArrowsDownUpIcon,
  ArrowsLeftRightIcon,
  BarcodeIcon,
  BicycleIcon,
  BuildingsIcon,
  CalculatorIcon,
  CameraIcon,
  ChartBarIcon,
  ClipboardIcon,
  ClipboardTextIcon,
  CubeIcon,
  CurrencyDollarIcon,
  GearIcon,
  HammerIcon,
  HandGrabbingIcon,
  ImageIcon,
  LightningIcon,
  ListChecksIcon,
  ListMagnifyingGlassIcon,
  MagnifyingGlassIcon,
  MapTrifoldIcon,
  MotorcycleIcon,
  PackageIcon,
  PlusMinusIcon,
  QrCodeIcon,
  ShareNetworkIcon,
  ShoppingCartIcon,
  SquaresFourIcon,
  StackIcon,
  TruckIcon,
  UsersThreeIcon,
  WarehouseIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import { PATHS_NAMES } from '../pathnames';
import { IResource, Role } from '../types';

const WAREHOUSE_EXECUTIVE_AND_ABOVE: Array<Role> = [
  'WAREHOUSE_ADMIN',
  'WAREHOUSE_MANAGER',
  'WAREHOUSE_EXECUTIVE',
];
const WAREHOUSE_MANAGER_AND_ABOVE: Array<Role> = ['WAREHOUSE_ADMIN', 'WAREHOUSE_MANAGER'];
// Put back an item and Repairs: warehouse and store staff. SCM checks the role at the chosen facility
// and refuses with 1624 otherwise.
const PUT_BACK_ROLES: Array<Role> = [
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'STORE_MANAGER',
  'STORE_ADMIN',
  'STORE_SUPER_ADMIN',
  'SUPER_ADMIN',
];
// Pickers reach STN Picking, Quick Pick and Order Picking to work their own picklists; the pages
// scope them to their own picklists (see PICKLIST_VIEW_ALL_ROLES in libs/pages picklist/roles.ts).
const PICKLIST_ACCESS_ROLES: Array<Role> = [...WAREHOUSE_EXECUTIVE_AND_ABOVE, 'PICKER'];
const WAREHOUSE_LOGISTICS_ROLES: Array<Role> = [
  'RIDER',
  'RIDER_MANAGER',
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'SUPER_ADMIN',
];
const HUB_OPERATIONS_LOGISTICS_ROLES: Array<Role> = [
  'RIDER_MANAGER',
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'SUPER_ADMIN',
];
const RIDER_MANAGEMENT_ROLES: Array<Role> = HUB_OPERATIONS_LOGISTICS_ROLES;
const RIDER_MANAGEMENT_MUTATION_ROLES: Array<Role> = ['RIDER_MANAGER', 'SUPER_ADMIN'];
// Facility ops and rider managers arrange return pickups (including Book with Porter) in Hub.
const RETURN_OPERATIONS_ROLES: Array<Role> = [
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'FACILITY_OPS',
  'RIDER_MANAGER',
];

// Hub menu (revamp, 2026-10-23): two blocks of groups. Routes, permissions and URLs are unchanged.
const ORDERS_BLOCK = { block: 'Orders & delivery', blockShort: 'Orders' };
const STOCK_BLOCK = { block: 'Stock & facility', blockShort: 'Stock' };
const ORDERS_GROUP = PATHS_NAMES.ORDERS_GROUP;
const RETURNS_GROUP = PATHS_NAMES.RETURNS_GROUP;
const FIND_STOCK_GROUP = PATHS_NAMES.FIND_STOCK_GROUP;
const FIX_STOCK_GROUP = PATHS_NAMES.FIX_STOCK_GROUP;
const SETTINGS_GROUP = PATHS_NAMES.SETTINGS;

// Display order; anything not listed keeps its place after the listed ones.
const MENU_ORDER: Array<string> = [
  'dashboard',
  PATHS_NAMES.TODAY,
  'ordercontrolcenter',
  ORDERS_GROUP,
  'packages',
  PATHS_NAMES.DASH_ORDERS,
  PATHS_NAMES.LOGISTICS,
  PATHS_NAMES.RIDER_HEALTH,
  PATHS_NAMES.OPEN_WORK_MAP,
  PATHS_NAMES.RIDER_ATTENDANCE_QR,
  PATHS_NAMES.CASH_HANDOVER,
  PATHS_NAMES.DELIVERY_SCAN_SUPPORT,
  PATHS_NAMES.RIDER_MANAGEMENT,
  PATHS_NAMES.MY_RIDES,
  RETURNS_GROUP,
  'returns',
  PATHS_NAMES.FACILITY_HANDOVER,
  PATHS_NAMES.RETURN_COURIER_EXCEPTIONS,
  'refunds-onhold',
  FIND_STOCK_GROUP,
  PATHS_NAMES.SCM_ITEMS,
  'inventory',
  PATHS_NAMES.SHELF_STOCK,
  PATHS_NAMES.PRODUCT_IMAGE_SEARCH,
  'store-images',
  PATHS_NAMES.SKU_ENQUIRY_HISTORY,
  FIX_STOCK_GROUP,
  PATHS_NAMES.SHELF_TRANSFER,
  PATHS_NAMES.SCM_STOCK_COUNT,
  PATHS_NAMES.SCM_STOCK_ADJUSTMENT,
  PATHS_NAMES.PUT_BACK,
  PATHS_NAMES.REPAIRS,
  PATHS_NAMES.SCM_REPAIRS,
  PATHS_NAMES.CYCLE_COUNT,
  PATHS_NAMES.OPS_TASKS,
  'inbound',
  PATHS_NAMES.SCM_RECEIVE,
  PATHS_NAMES.SCM_GRN,
  'grn',
  PATHS_NAMES.INCOMING_MANIFESTS,
  PATHS_NAMES.INCOMING_STOCK_TRANSFERS,
  PATHS_NAMES.PUTAWAY,
  'mtopurchaseorders',
  'outbound',
  PATHS_NAMES.ORDER_PICKING,
  PATHS_NAMES.BULK_PICK,
  PATHS_NAMES.OUTGOING_MANIFESTS,
  PATHS_NAMES.OUTGOING_STOCK_TRANSFERS,
  'vendormanagement',
  SETTINGS_GROUP,
  PATHS_NAMES.SHELVES,
];

const menuRank = (resource: IResource) => {
  const rank = MENU_ORDER.indexOf(resource.name);
  return rank === -1 ? MENU_ORDER.length : rank;
};

export const getWarehouseResources = (isAllFacilities?: boolean): Array<IResource> =>
  buildWarehouseResources(isAllFacilities)
    .map((resource, index) => ({ resource, index }))
    .sort((a, b) => menuRank(a.resource) - menuRank(b.resource) || a.index - b.index)
    .map(({ resource }) => resource);

const buildWarehouseResources = (isAllFacilities?: boolean): Array<IResource> => [
  {
    name: 'dashboard',
    list: '/',
    meta: {
      label: 'Coyu Hub',
      icon: <SquaresFourIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.TODAY,
    list: '/today',
    meta: {
      label: 'Today',
      icon: <ListChecksIcon />,
      // Rider Manager too (27 Oct): Today (now its Waiting on customer view) replaced Hub's Delivery follow-ups page,
      // which Rider Managers used. Fulfillment scopes Today with the Follow-ups read roles.
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
      ...ORDERS_BLOCK,
    },
  },
  {
    name: ORDERS_GROUP,
    meta: {
      label: 'Orders',
      // A group has no page of its own; showing it grants nothing (children keep their own roles).
      masterRoles: ['*'],
      icon: <CubeIcon />,
      ...ORDERS_BLOCK,
    },
  },
  {
    name: RETURNS_GROUP,
    meta: {
      label: 'Returns',
      // A group has no page of its own; showing it grants nothing (children keep their own roles).
      masterRoles: ['*'],
      icon: <ArrowsLeftRightIcon />,
      ...ORDERS_BLOCK,
    },
  },
  {
    name: FIND_STOCK_GROUP,
    meta: {
      label: 'Find stock',
      // A group has no page of its own; showing it grants nothing (children keep their own roles).
      masterRoles: ['*'],
      icon: <MagnifyingGlassIcon />,
      ...STOCK_BLOCK,
    },
  },
  {
    name: FIX_STOCK_GROUP,
    meta: {
      label: 'Stock Ops',
      // A group has no page of its own; showing it grants nothing (children keep their own roles).
      masterRoles: ['*'],
      icon: <WrenchIcon />,
      ...STOCK_BLOCK,
    },
  },
  {
    name: SETTINGS_GROUP,
    meta: {
      label: 'Settings',
      // A group has no page of its own; showing it grants nothing (children keep their own roles).
      masterRoles: ['*'],
      icon: <GearIcon />,
      ...STOCK_BLOCK,
    },
  },
  {
    name: 'packages',
    list: '/packages',
    show: '/packages/show/:id',
    meta: {
      parent: ORDERS_GROUP,
      label: 'Packages',
      icon: <CubeIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // Top level beside Today (24 Oct): the operators' other daily landing page.
    name: 'ordercontrolcenter',
    list: '/ordercontrolcenter',
    meta: {
      label: 'Control Center',
      icon: <ShareNetworkIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      // The undelivered tower (29 Oct): Rider Managers open Control Center for that tab only (the page filters tabs).
      permissions: { list: ['RIDER_MANAGER'] },
      ...ORDERS_BLOCK,
    },
  },
  {
    name: PATHS_NAMES.BULK_PICK,
    list: '/picklists',
    meta: {
      parent: 'outbound',
      label: 'STN Picking',
      icon: <LightningIcon />,
      masterRoles: PICKLIST_ACCESS_ROLES,
    },
  },
  {
    // On-demand pick: items already in hand sent out on a new STN, RTV or B2B party gatepass.
    name: PATHS_NAMES.QUICK_PICK,
    list: '/on-demand-pick',
    show: '/on-demand-pick/:id',
    meta: {
      parent: 'outbound',
      label: 'Quick Pick',
      icon: <LightningIcon />,
      masterRoles: PICKLIST_ACCESS_ROLES,
    },
  },
  {
    name: 'returns',
    list: '/returns',
    show: '/returns/show/:id',
    meta: {
      parent: RETURNS_GROUP,
      label: 'Returns',
      icon: <ArrowsLeftRightIcon />,
      masterRoles: RETURN_OPERATIONS_ROLES,
    },
  },
  {
    // Damaged / not-found units reported by fulfillment: inventory blocks, store counts,
    // warehouse resolution, DAMAGED close with putaway. Store managers resolve at stores.
    name: PATHS_NAMES.OPS_TASKS,
    list: '/ops-tasks',
    meta: {
      // Stage-only (SCM #353, not in the release): filed under Fix stock in the revamp menu.
      parent: FIX_STOCK_GROUP,
      label: 'Damaged & Missing',
      icon: <ClipboardTextIcon />,
      masterRoles: [...WAREHOUSE_EXECUTIVE_AND_ABOVE, 'STORE_MANAGER'],
    },
  },
  {
    name: PATHS_NAMES.RETURN_COURIER_EXCEPTIONS,
    list: '/returns/exceptions/cancelled-awb-movement',
    meta: {
      parent: RETURNS_GROUP,
      label: 'Return Courier Exceptions',
      icon: <ArrowsLeftRightIcon />,
      masterRoles: WAREHOUSE_MANAGER_AND_ABOVE,
    },
  },
  {
    name: 'refunds-onhold',
    list: '/refunds-onhold',
    show: '/refunds/show/:id',
    meta: {
      parent: RETURNS_GROUP,
      label: 'Refunds on hold',
      icon: <CurrencyDollarIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'inventory',
    list: '/inventory',
    meta: {
      parent: FIND_STOCK_GROUP,
      label: 'Inventory',
      icon: <PackageIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: PATHS_NAMES.CYCLE_COUNT,
    list: '/cycle-count',
    meta: {
      parent: FIX_STOCK_GROUP,
      label: 'Cycle count variance (Unicommerce)',
      icon: <ClipboardIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'vendormanagement',
    meta: {
      label: 'Vendors',
      icon: <TruckIcon />,
      ...STOCK_BLOCK,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'vendors',
    meta: {
      parent: 'vendormanagement',
      label: 'Vendors',
      icon: <BuildingsIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
    list: '/vendors',
    show: '/vendors/show/:id',
  },
  {
    name: 'skus',
    list: '/skus',
    meta: {
      parent: 'vendormanagement',
      label: 'Vendor Catalog',
      icon: <QrCodeIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: PATHS_NAMES.SKU_ENQUIRY_HISTORY,
    list: '/vendors/sku-enquiry-history',
    meta: {
      parent: FIND_STOCK_GROUP,
      label: 'SKU Enquiry History',
      icon: <ChartBarIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    // Stage-only menu (28 Oct): what comes in — receiving (GRN, transfers, manifests) and putaway.
    name: 'inbound',
    meta: {
      label: 'Receiving',
      icon: <WarehouseIcon />,
      ...STOCK_BLOCK,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // Stage-only menu (28 Oct): what goes out — order picking, STN picking, outgoing transfers and manifests.
    name: 'outbound',
    meta: {
      label: 'Outwarding',
      icon: <TruckIcon />,
      ...STOCK_BLOCK,
      // A group has no page of its own; showing it grants nothing (children keep their own roles).
      masterRoles: ['*'],
    },
  },
  {
    name: 'grn',
    list: '/grn',
    show: '/grn/show/:id',
    edit: '/grn/edit/:id',
    meta: {
      parent: 'inbound',
      label: 'GRN',
      icon: <ClipboardTextIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        edit: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        export_barcode: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        receive: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        import: ['IMPORT_ADMIN'],
      },
    },
  },
  {
    // The gate: receive a PO, DC or transfer manifest by scanning its paper, or pick it from what is expected here.
    name: PATHS_NAMES.SCM_RECEIVE,
    list: '/inward/receive',
    // A transfer's pieces scanned in onto the receiving shelf.
    show: '/inward/receive/:id',
    meta: {
      parent: 'inbound',
      label: 'Gate Entry',
      icon: <TruckIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // B2B shipments coming here: scan in the pieces of received ones, or receive one not received yet.
    name: PATHS_NAMES.SCM_INCOMING_SHIPMENTS,
    list: '/inward/incoming',
    meta: {
      parent: 'inbound',
      label: 'Incoming Shipments',
      icon: <TruckIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // A rider back at the hub: take his TAH returns, Dash returns and cash. Same roles as Cash Handover.
    name: PATHS_NAMES.RIDER_HANDOVER,
    list: '/inward/rider-handover',
    meta: {
      parent: 'inbound',
      label: 'Rider Handover',
      icon: <MotorcycleIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
  {
    // Who did what at the facility on a day: receiving, putaway, picking, moves and counts.
    name: PATHS_NAMES.SCM_ACTIVITY,
    list: '/inward/activity',
    meta: {
      parent: 'inbound',
      label: 'Activity',
      icon: <ClipboardIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // SCM-owned GRN: scan pieces and QC them on a phone.
    name: PATHS_NAMES.SCM_GRN,
    list: '/inward/grn',
    show: '/inward/grn/:id',
    meta: {
      parent: 'inbound',
      label: 'Pending GRNs',
      icon: <BarcodeIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // SCM-owned putaway of GRN pieces and incoming gatepasses onto shelves.
    name: PATHS_NAMES.PUTAWAY,
    list: '/inward/putaway',
    show: '/inward/putaway/:id',
    meta: {
      parent: 'inbound',
      label: 'Pending Putaways',
      icon: <StackIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // SCM order picking: picklists over the units reserved for packages (and STNs).
    name: PATHS_NAMES.ORDER_PICKING,
    list: '/inward/picking',
    show: '/inward/picking/:id',
    meta: {
      parent: 'outbound',
      label: 'Order Picking',
      icon: <HandGrabbingIcon />,
      // Pickers too: Start picking and their own picklists (the page scopes them; SCM checks no role).
      masterRoles: PICKLIST_ACCESS_ROLES,
    },
  },
  {
    // Damaged, repairable units held on quarantine shelves until repaired or scrapped.
    name: PATHS_NAMES.SCM_REPAIRS,
    list: '/inward/repairs',
    meta: {
      parent: FIX_STOCK_GROUP,
      label: 'Repaired pieces',
      icon: <HammerIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // Cycle-count results: units missing or found on a shelf, and excess units to review.
    name: PATHS_NAMES.SCM_STOCK_COUNT,
    list: '/inward/stock-count',
    meta: {
      parent: FIX_STOCK_GROUP,
      label: 'Cycle count',
      icon: <CalculatorIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // Manual stock adjustments with a reason: add units that turned up, write off units that are gone; each undoable.
    name: PATHS_NAMES.SCM_STOCK_ADJUSTMENT,
    list: '/inward/stock-adjustment',
    meta: {
      parent: FIX_STOCK_GROUP,
      label: 'Stock adjustment',
      icon: <PlusMinusIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // Every item SCM holds at the facility, by shelf or by SKU, with each item's history. Read-only.
    name: PATHS_NAMES.SCM_ITEMS,
    list: '/inward/items',
    meta: {
      parent: FIND_STOCK_GROUP,
      label: 'Items',
      icon: <ListMagnifyingGlassIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'mtopurchaseorders',
    list: '/mtopurchaseorders',
    meta: {
      parent: 'inbound',
      label: 'MTO Purchase Orders',
      icon: <ShoppingCartIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    // Permission-only (no route/menu): gates the DC facility-transfer action on the Hub DC show page.
    name: 'deliverychallans',
    meta: {
      permissions: {
        transfer_facility: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  ...(isAllFacilities
    ? [
        {
          name: PATHS_NAMES.INCOMING_STOCK_TRANSFERS,
          list: '/incoming-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Stock Transfers',
            icon: <ArrowsDownUpIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]
    : [
        {
          name: PATHS_NAMES.INCOMING_STOCK_TRANSFERS,
          list: '/incoming-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Incoming Stock Transfers',
            icon: <ArrowsDownUpIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
        {
          name: PATHS_NAMES.OUTGOING_STOCK_TRANSFERS,
          list: '/outgoing-stock-transfers',
          meta: {
            parent: 'outbound',
            label: 'Outgoing Stock Transfers',
            icon: <TruckIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]),
  {
    // Permission-only detail route used from package Stock Transfer "View" links.
    name: PATHS_NAMES.STOCK_TRANSFER,
    edit: '/stocktransfer/edit/:id',
    meta: {
      permissions: {
        edit: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  ...(isAllFacilities
    ? []
    : [
        {
          name: PATHS_NAMES.INCOMING_MANIFESTS,
          list: '/incoming-manifests',
          meta: {
            parent: 'inbound',
            label: 'Incoming Manifests',
            icon: <ClipboardTextIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
        {
          name: PATHS_NAMES.OUTGOING_MANIFESTS,
          list: '/outgoing-manifests',
          meta: {
            parent: 'outbound',
            label: 'B2B Shipments',
            icon: <ClipboardIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
        {
          // Courier pickups: packed customer packages scanned into a manifest and handed to the courier.
          name: PATHS_NAMES.OUTGOING_COURIER,
          list: '/outgoing-courier',
          meta: {
            parent: 'outbound',
            label: 'Courier Pickup',
            icon: <TruckIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]),
  {
    name: PATHS_NAMES.MANIFESTS,
    create: '/manifests/create',
    show: '/manifests/:id/show',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.GATEPASS_ORDERS,
    show: '/gatepass-orders/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'packages',
    show: '/packages/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'lineitems',
    show: '/lineitems/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      permissions: {
        transfer_facility: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'orders',
    show: '/orders/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'refunds',
    show: '/refunds/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.FACILITIES,
    show: '/facilities/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.B2B_CUSTOMERS,
    show: '/b2b-customer/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  { name: 'skuupload' },
  { name: 'pincode' },
  {
    name: PATHS_NAMES.PRODUCT_IMAGE_SEARCH,
    list: '/utility/product-image-search',
    meta: {
      parent: FIND_STOCK_GROUP,
      label: 'Product Image Search',
      icon: <ImageIcon />,
      masterRoles: ['*'],
    },
  },
  {
    name: 'store-images',
    list: '/utility/store-images',
    meta: {
      parent: FIND_STOCK_GROUP,
      label: 'Store Images',
      icon: <CameraIcon />,
      masterRoles: ['*'],
    },
  },
  {
    // SCM shelf master: add, edit, remove shelves and print their labels.
    name: PATHS_NAMES.SHELVES,
    list: '/utility/shelves',
    meta: {
      parent: SETTINGS_GROUP,
      label: 'Shelves',
      icon: <SquaresFourIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // SCM shelf-wise stock, read-only, with each row's change history.
    name: PATHS_NAMES.SHELF_STOCK,
    list: '/utility/shelf-stock',
    meta: {
      parent: FIND_STOCK_GROUP,
      label: 'View Stock',
      icon: <StackIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.SHELF_TRANSFER,
    list: '/utility/shelf-transfer',
    meta: {
      parent: FIX_STOCK_GROUP,
      label: 'Move Stock',
      icon: <ArrowsDownUpIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      // The workflow is a single list route; do not grant resource-wide wildcard actions.
      permissions: {
        list: ['SHELF_TRANSFER'],
      },
    },
  },
  {
    name: PATHS_NAMES.PUT_BACK,
    list: '/utility/put-back',
    meta: {
      parent: FIX_STOCK_GROUP,
      label: 'Put Back an Item',
      icon: <ArrowUUpLeftIcon />,
      masterRoles: PUT_BACK_ROLES,
    },
  },
  {
    // Pieces a picker reported Repairable: repair, then put back (or can't be repaired). Same people as Put back.
    name: PATHS_NAMES.REPAIRS,
    list: '/utility/repairs',
    meta: {
      parent: FIX_STOCK_GROUP,
      label: 'Repairs & Restock',
      icon: <WrenchIcon />,
      masterRoles: PUT_BACK_ROLES,
    },
  },
  {
    // Kept beside Today until its daily users have moved over (Bhaskar, 24 Oct).
    name: PATHS_NAMES.DASH_ORDERS,
    list: '/dash-orders',
    meta: {
      parent: ORDERS_GROUP,
      label: 'SELF Orders',
      icon: <LightningIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    // Riders group. /logistics itself redirects to the Rider Control Centre (it used to render blank).
    name: PATHS_NAMES.LOGISTICS,
    meta: {
      label: 'Riders',
      icon: <BicycleIcon />,
      ...ORDERS_BLOCK,
      masterRoles: WAREHOUSE_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.MY_RIDES,
    list: '/logistics/my-rides',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'My Rides',
      icon: <BicycleIcon />,
      masterRoles: WAREHOUSE_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.OPEN_WORK_MAP,
    list: '/logistics/open-work-map',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Open Work Map',
      icon: <MapTrifoldIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.RIDER_MANAGEMENT,
    list: '/logistics/riders',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Rider Management',
      icon: <UsersThreeIcon />,
      masterRoles: RIDER_MANAGEMENT_ROLES,
      permissions: {
        list: RIDER_MANAGEMENT_ROLES,
        create: RIDER_MANAGEMENT_MUTATION_ROLES,
        edit: RIDER_MANAGEMENT_MUTATION_ROLES,
        delete: RIDER_MANAGEMENT_MUTATION_ROLES,
      },
    },
  },
  {
    name: PATHS_NAMES.CASH_HANDOVER,
    list: '/logistics/cash-handover',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Cash Handover',
      icon: <CurrencyDollarIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
  {
    // No screen of its own any more (Rider Handover receives); still the permission for the rider's /receive link.
    name: PATHS_NAMES.FACILITY_HANDOVER,
    meta: {
      hide: true,
      parent: RETURNS_GROUP,
      label: 'Receive parcels',
      icon: <PackageIcon />,
      masterRoles: [...WAREHOUSE_EXECUTIVE_AND_ABOVE, 'SUPER_ADMIN'],
    },
  },
  {
    name: PATHS_NAMES.DELIVERY_SCAN_SUPPORT,
    list: '/logistics/delivery-scan-support',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Doorstep scan support',
      icon: <QrCodeIcon />,
      permissions: { list: ['RIDER_MANAGER', ...WAREHOUSE_MANAGER_AND_ABOVE, 'SUPER_ADMIN'] },
    },
  },
  {
    name: PATHS_NAMES.RIDER_ATTENDANCE_QR,
    list: '/logistics/attendance-qr',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Rider attendance',
      icon: <QrCodeIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.RIDER_HEALTH,
    list: '/logistics/rider-health',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Rider Control Centre',
      icon: <BicycleIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
];

// Backward compatibility - keep the old static array
export const WAREHOUSE_RESOURCES: Array<IResource> = getWarehouseResources(false);
